"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Icon, Pill } from "@/components/Icons";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useStock } from "@/context/StockContext";
import type {
  ProduitAchete,
  ProduitVendu,
  VenteAchatProps,
} from "@/lib/pennylane/assoc-types";
import type { PackComposition } from "@/lib/pennylane/pack-types";

type Filtre = "tous" | "auto" | "manuel" | "valides" | "a-faire" | "ignores";

export function AssociationsView() {
  const { showToast } = useStock();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ventes, setVentes] = useState<ProduitVendu[]>([]);
  const [achats, setAchats] = useState<ProduitAchete[]>([]);
  const [packs, setPacks] = useState<PackComposition[]>([]);
  const [rows, setRows] = useState<VenteAchatProps[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<Filtre>("a-faire");
  const [q, setQ] = useState("");
  const listKey = `${filtre}\0${q}`;
  const [seenListKey, setSeenListKey] = useState(listKey);
  const [stickyIds, setStickyIds] = useState<string[]>([]);
  if (seenListKey !== listKey) {
    setSeenListKey(listKey);
    setStickyIds([]);
  }

  const venteById = useMemo(() => new Map(ventes.map((v) => [v.id, v])), [ventes]);
  const achatById = useMemo(() => new Map(achats.map((a) => [a.id, a])), [achats]);
  const packByVente = useMemo(() => new Map(packs.map((p) => [p.packId, p])), [packs]);

  const sync = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/pennylane/associations");
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Impossible de construire les associations");
        return;
      }
      setVentes(data.ventes ?? []);
      setAchats(data.achats ?? []);
      setPacks(data.packs ?? []);
      setRows(data.rows ?? []);
      setUpdatedAt(data.updatedAt ?? null);
      showToast(
        `${data.stats?.achats ?? 0} achetés · ${data.stats?.ventes ?? 0} vendus · ${data.stats?.auto ?? 0} auto`,
      );
    } catch {
      showToast("Erreur réseau");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    void sync();
  }, [sync]);

  const patchRow = async (
    rowId: string,
    patch: { venteId?: string | null; validated?: boolean; ignored?: boolean },
  ) => {
    if (patch.validated !== undefined || patch.ignored !== undefined) {
      setStickyIds((prev) => (prev.includes(rowId) ? prev : [...prev, rowId]));
    }
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const ignored = patch.ignored ?? r.ignored;
        return {
          ...r,
          venteId: patch.venteId !== undefined ? patch.venteId : r.venteId,
          ignored,
          validated: ignored ? false : (patch.validated ?? r.validated),
          matchType:
            patch.venteId !== undefined ? "manuel" : (r.matchType ?? "manuel"),
        };
      }),
    );
    const res = await fetch("/api/pennylane/associations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rowId, ...patch }),
    });
    if (res.ok) {
      const data = await res.json();
      setUpdatedAt(data.updatedAt ?? new Date().toISOString());
    } else {
      showToast("Enregistrement impossible");
    }
  };

  const validateAll = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/pennylane/associations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ validateAll: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || "Validation impossible");
        return;
      }
      setRows(data.rows ?? []);
      setUpdatedAt(data.updatedAt ?? null);
      showToast(
        `${(data.rows ?? []).filter((r: VenteAchatProps) => r.validated).length} associations validées`,
      );
    } finally {
      setSaving(false);
    }
  };

  const qTokens = useMemo(
    () =>
      q
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .split(/[^a-z0-9]+/i)
        .filter(Boolean),
    [q],
  );

  const matchQ = useCallback(
    (texte: string) => {
      if (!qTokens.length) return true;
      const hay = texte
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      return qTokens.every((t) => hay.includes(t));
    },
    [qTokens],
  );

  const filtered = useMemo(() => {
    const sticky = new Set(stickyIds);
    return rows.filter((r) => {
      let matches = true;
      if (filtre === "auto" && (r.matchType !== "auto" || r.ignored)) matches = false;
      if (filtre === "manuel" && (r.matchType !== "manuel" || r.ignored)) matches = false;
      if (filtre === "valides" && (!r.validated || r.ignored)) matches = false;
      if (filtre === "ignores" && !r.ignored) matches = false;
      if (filtre === "a-faire" && (r.validated || r.ignored)) matches = false;
      if (!matches && !sticky.has(r.id)) return false;
      if (!qTokens.length) return true;
      const a = achatById.get(r.achatId);
      const v = r.venteId ? venteById.get(r.venteId) : null;
      const hay = `${a?.texte ?? ""} ${a?.fournisseur ?? ""} ${v?.texte ?? ""}`;
      return matchQ(hay);
    });
  }, [rows, filtre, stickyIds, achatById, venteById, qTokens, matchQ]);

  const stats = useMemo(
    () => ({
      total: rows.length,
      auto: rows.filter((r) => r.matchType === "auto" && !r.ignored).length,
      manuel: rows.filter((r) => r.matchType === "manuel" && !r.ignored).length,
      aFaire: rows.filter((r) => !r.validated && !r.ignored).length,
      valides: rows.filter((r) => r.validated && !r.ignored).length,
      ignores: rows.filter((r) => r.ignored).length,
    }),
    [rows],
  );

  /** Combien d'achats pointent déjà vers chaque vente (info UI). */
  const liensParVente = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      if (!r.venteId || r.ignored) continue;
      m.set(r.venteId, (m.get(r.venteId) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  const venteOptions = useMemo(
    () =>
      [...ventes]
        .sort((a, b) => a.texte.localeCompare(b.texte, "fr", { sensitivity: "base" }))
        .map((v) => ({
          id: v.id,
          label: v.texte,
          meta: `${v.qte} vendus · ${v.factures} fact.`,
        })),
    [ventes],
  );

  return (
    <>
      <p className="muted" style={{ maxWidth: "70ch", marginBottom: 14 }}>
        Relie chaque produit <b>acheté</b> (fournisseurs) au produit <b>vendu</b> correspondant.
        Plusieurs achats peuvent pointer vers le <b>même</b> produit vendu. Les{" "}
        <b>packs</b> ne sont plus proposés ici : leurs quantités sont reportées sur les
        produits unitaires.
      </p>

      <div className="assoc-meta">
        <span>
          Stockage : <b className="mono">data/vente-achat-associations.json</b>
        </span>
        <span>
          Achetés : <b>{achats.length}</b>
        </span>
        <span>
          Vendus : <b>{ventes.length}</b>
          {packs.length
            ? ` · ${packs.length} pack${packs.length > 1 ? "s" : ""} exclus (éclatés)`
            : ""}
        </span>
        {updatedAt && (
          <span>
            Dernier enreg. :{" "}
            <b>
              {new Date(updatedAt).toLocaleString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </b>
          </span>
        )}
      </div>

      <div className="toolbar" style={{ marginBottom: 10 }}>
        <div className="search">
          <Icon name="search" />
          <input
            type="search"
            placeholder="Achat, fournisseur, produit vendu…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="chips">
          {(
            [
              ["a-faire", `À faire (${stats.aFaire})`],
              ["auto", `Auto (${stats.auto})`],
              ["manuel", `Manuel (${stats.manuel})`],
              ["valides", `Validées (${stats.valides})`],
              ["ignores", `Ignorées (${stats.ignores})`],
              ["tous", `Tous (${stats.total})`],
            ] as const
          ).map(([id, lib]) => (
            <button
              key={id}
              className="chip"
              type="button"
              aria-pressed={filtre === id}
              onClick={() => {
                if (filtre === id) setStickyIds([]);
                else setFiltre(id);
              }}
            >
              {lib}
            </button>
          ))}
        </div>
        <button className="btn small" type="button" disabled={loading} onClick={() => void sync()}>
          <Icon name="sync" /> {loading ? "…" : "Recalculer"}
        </button>
        <button
          className="btn primary small"
          type="button"
          disabled={saving}
          onClick={() => void validateAll()}
        >
          <Icon name="check" /> Tout valider
        </button>
      </div>

      <div className="assoc-sheet">
        <table>
          <thead>
            <tr>
              <th className="numcol">#</th>
              <th className="matchcol">Match</th>
              <th>Produit acheté</th>
              <th>Produit vendu</th>
              <th className="statcol">Statut</th>
              <th className="actcol">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row, idx) => {
              const achat = achatById.get(row.achatId);
              const current = row.venteId ? venteById.get(row.venteId) : null;
              const nLiens = row.venteId ? (liensParVente.get(row.venteId) ?? 0) : 0;
              return (
                <tr
                  key={row.id}
                  className={row.ignored ? "is-ignored" : row.validated ? "is-ok" : ""}
                >
                  <td className="numcol">{idx + 1}</td>
                  <td className="matchcol">
                    <span className="tag">
                      {row.matchType === "auto"
                        ? "Auto"
                        : row.matchType === "manuel"
                          ? "Manuel"
                          : "—"}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{achat?.texte ?? "—"}</div>
                    <span className="cell-meta">
                      {achat
                        ? `${achat.fournisseur} · ${achat.qte} achetés · ${achat.factures} fact.`
                        : ""}
                    </span>
                  </td>
                  <td>
                    <SearchableSelect
                      options={venteOptions}
                      value={row.venteId}
                      disabled={Boolean(row.ignored)}
                      placeholder="Ex. coude 250, manchette…"
                      onChange={(venteId) => {
                        void patchRow(row.id, { venteId, validated: false });
                      }}
                    />
                    {current && (
                      <span className="cell-meta">
                        {current.qte} vendus · {current.factures} fact.
                        {nLiens > 1 ? ` · ${nLiens} achats liés` : ""}
                        {packByVente.has(current.id)
                          ? ` · pack (${packByVente.get(current.id)!.composants.length} compos.)`
                          : ""}
                      </span>
                    )}
                  </td>
                  <td className="statcol">
                    {row.ignored ? (
                      <Pill kind="info">Ignorée</Pill>
                    ) : row.validated ? (
                      <Pill kind="ok">Validée</Pill>
                    ) : (
                      <Pill kind="warn">À faire</Pill>
                    )}
                  </td>
                  <td className="actcol">
                    <span className="acts">
                      {!row.ignored && (
                        <button
                          className={`btn tiny ${row.validated ? "" : "primary"}`}
                          type="button"
                          title={row.validated ? "Dévalider" : "Valider"}
                          disabled={!row.validated && !row.venteId}
                          onClick={() => void patchRow(row.id, { validated: !row.validated })}
                        >
                          {row.validated ? "↩" : "✓"}
                        </button>
                      )}
                      <button
                        className="btn tiny"
                        type="button"
                        title={row.ignored ? "Réintégrer" : "Ignorer"}
                        onClick={() => {
                          const next = !row.ignored;
                          void patchRow(row.id, { ignored: next });
                          showToast(next ? "Ligne ignorée" : "Ligne réintégrée");
                        }}
                      >
                        {row.ignored ? "↩" : "⊘"}
                      </button>
                    </span>
                  </td>
                </tr>
              );
            })}
            {!filtered.length && !loading && (
              <tr>
                <td colSpan={6} className="muted" style={{ padding: 20, textAlign: "center" }}>
                  Aucune ligne pour ce filtre. Importe d&apos;abord ventes et achats via Synchroniser.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={6} className="muted" style={{ padding: 20, textAlign: "center" }}>
                  Calcul des associations achats → ventes…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
